// src/components/compliance/modals/ValidationFailedModal.jsx

import React from "react";
import {
  cleanValue,
  formatTimestamp,
  getTaskStatusLabel,
  normalizeText,
  buildControlLabel,
} from "../../../utils/complianceFormatters";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import TaskQueueCard from "./shared/TaskQueueCard";

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 16,
};

function getTaskRuleLabel(task = {}) {
  return buildControlLabel(task.control_id || task.controlId, task.rule_id || task.ruleId);
}

function getFailureReason(task = {}) {
  const metadata = task.task_metadata || task.metadata || {};
  const lastValidation = metadata.last_validation || metadata.lastValidation || {};
  return (
    task.validation_reason ||
    task.failure_reason ||
    lastValidation.reason ||
    lastValidation.failure_reason ||
    lastValidation.message ||
    "Validation failed against the latest source evidence. Review the task and complete another fix pass before closing."
  );
}

function getTaskResource(task = {}) {
  return cleanValue(task.resource_id || task.resourceId || task.resource || task.target_resource, "—");
}

function getTaskPriority(task = {}) {
  const priority = task.priority;
  return priority === undefined || priority === null || priority === "" ? "—" : `P${priority}`;
}

function getOwner(task = {}) {
  return cleanValue(task.owner_name || task.ownerName || task.owner_user_id || task.ownerUserId, "Unassigned");
}

function buildDriftParams(task = {}, framework) {
  return {
    framework: task.framework || framework,
    controlId: task.control_id || task.controlId || undefined,
    ruleId: task.rule_id || task.ruleId || undefined,
    resourceId: task.resource_id || task.resourceId || undefined,
  };
}

export default function ValidationFailedModal({
  open,
  framework,
  validationFailedTasks = [],
  onClose,
  onReviewTask,
  onFixPlan,
  onViewGuidance,
  onOpenDrift,
}) {
  if (!open) return null;

  const tasks = Array.isArray(validationFailedTasks) ? validationFailedTasks : [];
  const unassignedCount = tasks.filter((task) => normalizeText(getOwner(task)) === "unassigned").length;
  const highPriorityCount = tasks.filter((task) => Number(task.priority || 0) >= 100).length;
  const overdueCount = tasks.filter((task) => task.is_overdue || normalizeText(task.sla_state) === "overdue").length;

  return (
    <ModalShell
      open={open}
      ariaLabel="Validation failed remediation tasks"
      eyebrow="OASYS · VALIDATION FAILED"
      title="Validation Failed"
      subtitle="Review remediation tasks that did not pass the latest validation check. These items need another fix pass before they can be closed."
      onClose={onClose}
      maxWidth="1120px"
      accentBorder="rgba(248, 113, 113, 0.48)"
    >
      <div style={metricGridStyle}>
        <MetricTile label="Failed Tasks" value={tasks.length} sub={`${framework} validation failures`} color="#f87171" />
        <MetricTile
          label="High Priority"
          value={highPriorityCount}
          sub="priority fix pass"
          color={highPriorityCount > 0 ? "#f87171" : "#34d399"}
        />
        <MetricTile
          label="Overdue"
          value={overdueCount}
          sub="SLA breached"
          color={overdueCount > 0 ? "#f87171" : "#34d399"}
        />
        <MetricTile
          label="Unassigned"
          value={unassignedCount}
          sub="needs owner"
          color={unassignedCount > 0 ? "#fbbf24" : "#34d399"}
        />
      </div>

      <ModalSection
        title="Failed Validation Activity"
        description="Use Review / Manage to update the task, Fix Plan to complete the remediation pass, or Guidance to view context."
        badge={`${tasks.length} shown`}
        badgeTone={tasks.length > 0 ? "danger" : "ok"}
      >
        {tasks.length === 0 ? (
          <div className="muted" style={{ padding: 14 }}>
            No validation-failed remediation tasks are currently loaded for this framework.
          </div>
        ) : (
          tasks.map((task, index) => {
            const title = task.title || task.task_title || getTaskRuleLabel(task);
            const failureReason = getFailureReason(task);
            const resource = getTaskResource(task);

            return (
              <TaskQueueCard
                key={task.id || `${title}-${index}`}
                title={title}
                chips={[
                  { label: "Failed", tone: "danger" },
                  { label: task.is_overdue ? "Overdue" : getTaskStatusLabel(task.status), tone: task.is_overdue ? "danger" : "default" },
                  { label: getOwner(task), tone: getOwner(task) === "Unassigned" ? "warn" : "ok" },
                ]}
                dueLabel={formatTimestamp(task.validated_at || task.updated_at || task.created_at)}
                meta={[
                  { label: "Control", value: cleanValue(task.control_id || task.controlId, "—"), wrap: true },
                  { label: "Rule", value: cleanValue(task.rule_id || task.ruleId, "—"), wrap: true },
                  { label: "Resource", value: resource, wrap: true },
                ]}
                context={`${failureReason} · ${task.framework || framework} · ${getTaskRuleLabel(task)} · ${getTaskPriority(task)}`}
                contextLabel="Failure reason"
                actions={[
                  { key: "review", label: "Review / Manage", onClick: () => onReviewTask(task, { returnTo: "validation_failed" }) },
                  { key: "fix", label: "Fix Plan", onClick: () => onFixPlan(task, "task", { returnTo: "validation_failed" }) },
                  { key: "guidance", label: "Guidance", onClick: () => onViewGuidance(task, { returnTo: "validation_failed" }) },
                  { key: "drift", label: "Open in Drift", onClick: () => onOpenDrift(buildDriftParams(task, framework)) },
                ]}
              />
            );
          })
        )}
      </ModalSection>
    </ModalShell>
  );
}
